import type { InstallOutcome } from '@/compositions/gaUtils'

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed', platform: string }>
}

export const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
export const showInstallPrompt = ref(false)
export const disableInstallPrompt = ref(false)
export const canInstallAsApp = ref(false)
export const supportedInstallAsApp = ref(false)
export const showConfirmDisableInstallPrompt = ref(false)
export const checkBoxDisableInstallPrompt = ref(false)

export type InstallBtnSection = 'installBtn' | 'footer'
let promptPending = false

// install button click (game page / footer): browser install dialog when available (Chromium), otherwise our
// "add to home screen" instructions dialog; GA: install_btn_click + install_prompt_result (browser dialog only),
// install method / outcome sent as gs_context
export async function openInstallPrompt(section: InstallBtnSection, gsGame?: Games.GameKeyType) {
  if (promptPending) {
    return // browser install dialog already open (double click)
  }
  const installEvent = canInstallAsApp.value ? deferredPrompt.value : null
  gaUtils.gaInstallBtnClick({ gs_section: section, gs_game: gsGame }, installEvent ? 'native_prompt' : 'instructions')
  if (!installEvent) {
    showInstallPrompt.value = true
    return
  }
  promptPending = true
  let outcome: InstallOutcome | null = null
  try {
    // prompt() must run synchronously in the click handler (user gesture) - no await before it
    const promptShown = installEvent.prompt()
    await promptShown
    outcome = (await installEvent.userChoice).outcome
  } catch {
    // prompt() can be used only once / may be rejected by the browser - no outcome to report
  } finally {
    promptPending = false
    // the browser install dialog can't be shown again with the same event - the button hides until Chrome
    // fires beforeinstallprompt again
    deferredPrompt.value = null
    canInstallAsApp.value = false
  }
  if (outcome) {
    gaUtils.gaInstallPromptResult({ gs_section: section, gs_game: gsGame }, outcome)
  }
}
